import { z } from 'zod';

/**
 * The reason a call exists, split into two concerns:
 *
 *   - **Intent** — what the call is for. Pure string discriminant. On an
 *     outbound call it picks the v2 workflow that runs (`OutboundCall.workflowIntent`
 *     in apps/livekit/src/agents/runtime/direction/ → `definitionFor` in
 *     runtime/workflow/). An inbound call always runs `identify_caller`.
 *   - **Context** — intent-specific reference data preloaded by the
 *     caller's orchestration layer and read by that workflow.
 *
 * Adding a new intent: add a variant here and the workflow definition that
 * serves it in the same change.
 */

/**
 * Only the intents a v2 workflow serves (`definitionFor` in
 * apps/livekit/src/agents/runtime/workflow/definitions.ts). An intent comes
 * back here with the workflow that runs it, never before (ADR-310).
 */
export const INTENTS = ['schedule_referral', 'general_inquiry'] as const;
export type Intent = (typeof INTENTS)[number];
export const intentSchema = z.enum(INTENTS);

// Per-intent context schemas. Each is a plain Zod object so the
// top-level union doesn't need to care about per-variant internals.

/** Referral preloaded by the orchestration layer (outbound typical). */
export const scheduleReferralContextSchema = z.object({
  referralId: z.string().uuid(),
  /**
   * Pre-rendered referral paragraph. Still emitted for V1 agents that
   * inject it into the system prompt. V2 ignores this and reads the
   * structured fields below — the prose blob carries CPT/billing
   * language we don't want the agent paraphrasing aloud.
   */
  referralContext: z.string(),
  /** Optional: referring provider's display name (e.g. "Dr. Samuel Wilson"). */
  referringProviderName: z.string().optional(),
  /** Optional: referring practice (e.g. "Heritage Victor Valley Medical Group"). */
  referringPracticeName: z.string().optional(),
  /**
   * Optional: destination specialty/practice the patient is being
   * referred TO (e.g. "pulmonology and sleep"). Spoken in the post-gate
   * disclosure. When absent, the disclosure degrades to a generic
   * referral line. Structured (not parsed from `referralContext`) so the
   * spoken specialty is exact — see docs/voice-scheduling-form.md.
   */
  specialty: z.string().optional(),
  /**
   * Optional: whether the patient is NEW to this practice. Gates the
   * new-patient reminders. When absent, the referral flow treats the
   * patient as new (the common case) until this is populated upstream.
   */
  isNewPatient: z.boolean().optional(),
  /**
   * Operator-selected scheduling constraints (added for provider/type-scoped
   * booking). The OPERATOR — not the agent — picks who and what the
   * appointment is for in the schedule drawer; these ride the call reason so
   * both the SIP-outbound and browser-test dispatch paths carry them into
   * the agent's CallContext.
   *
   *   - `providerId`: the agent offers ONLY this provider's slots (the slot
   *     search is hard-locked to it; the model cannot propose another).
   *   - `locationId`: the warmed location; scopes the slot search alongside
   *     the provider so only the pulled location's openings are offered.
   *   - `appointmentTypeId`: the chosen appointment type. Recorded for
   *     context; with Office Ally's type-agnostic slot scrape it does NOT
   *     filter slots — it only drives `durationMinutes` below.
   *   - `durationMinutes`: resolved from the appointment type at dispatch;
   *     used as the booking duration when the slot is confirmed.
   *
   * All optional so legacy / unscoped scheduling calls still validate.
   */
  providerId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  appointmentTypeId: z.string().uuid().optional(),
  durationMinutes: z.number().int().positive().optional(),
});

/** Inbound call with no pre-known reason. No context payload. */
export const generalInquiryContextSchema = z.object({}).strict();

/**
 * A call reason: intent + its typed context. Every caller of the
 * registry passes one of these whole; narrowing happens internally via
 * the dispatcher functions, not at the call site. That removes the
 * `as any` / `as never` casts that the old flat shape required.
 */
export const callReasonSchema = z.discriminatedUnion('intent', [
  z.object({ intent: z.literal('schedule_referral'), context: scheduleReferralContextSchema }),
  z.object({
    intent: z.literal('general_inquiry'),
    context: generalInquiryContextSchema.default({}),
  }),
]);

export type CallReason = z.infer<typeof callReasonSchema>;

/** Context shape for a given intent. Enables `ReasonDefinition<I>` typing. */
export type ContextFor<I extends Intent> = Extract<CallReason, { intent: I }>['context'];

/**
 * Call direction. Determines the primary agent's opener and identity-
 * verification posture. Specialists never see this.
 */
export const callDirectionSchema = z.enum(['inbound', 'outbound']);
export type CallDirection = z.infer<typeof callDirectionSchema>;
